import React from 'react'
import {AppState} from 'react-native'
import {useQueryClient} from '@tanstack/react-query'

import {TapFeedUpdatesEventBus} from './agent'
import {useSession} from '#/state/session'

const TapFeedUpdatesContext = React.createContext<TapFeedUpdatesEventBus | null>(
  null,
)

export function useTapFeedUpdates() {
  return React.useContext(TapFeedUpdatesContext)
}

export function TapFeedUpdatesProvider({
  children,
}: {
  children: React.ReactNode
}) {
  const {currentAccount} = useSession()

  if (!currentAccount) {
    return (
      <TapFeedUpdatesContext.Provider value={null}>
        {children}
      </TapFeedUpdatesContext.Provider>
    )
  }

  return (
    <TapFeedUpdatesProviderInner>{children}</TapFeedUpdatesProviderInner>
  )
}

export function TapFeedUpdatesProviderInner({
  children,
}: {
  children: React.ReactNode
}) {
  const {currentAccount} = useSession()
  const queryClient = useQueryClient()
  const accessToken = currentAccount?.accessJwt

  const [bus] = React.useState(
    () =>
      new TapFeedUpdatesEventBus({
        accessToken,
        queryClient,
      }),
  )

  React.useEffect(() => {
    bus.resume()

    return () => {
      bus.suspend()
    }
  }, [bus])

  React.useEffect(() => {
    const handleAppStateChange = (nextAppState: string) => {
      if (nextAppState === 'active') {
        bus.resume()
      } else {
        bus.background()
      }
    }

    const sub = AppState.addEventListener('change', handleAppStateChange)

    return () => {
      sub.remove()
    }
  }, [bus])

  return (
    <TapFeedUpdatesContext.Provider value={bus}>
      {children}
    </TapFeedUpdatesContext.Provider>
  )
}
